import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useToast } from '@shared/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { SortConfig } from '@components/ui/VirtualTable';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { useOrders } from './useOrders';
import { useLogisticStatusCounts } from './useLogisticStatusCounts';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { OrdersApiAdapter, StatsApiAdapter } from '../adapters';

const getDateFromConditions = (conditions: FilterConditionOutput[], column: string): { from?: string; to?: string } => {
  const from = conditions.find(
    (condition) => condition.column === column && condition.action === 'greater_equal',
  )?.value;
  const to = conditions.find((condition) => condition.column === column && condition.action === 'less_equal')?.value;

  return {
    from: from ? String(from) : undefined,
    to: to ? String(to) : undefined,
  };
};

const getInConditionValues = (conditions: FilterConditionOutput[], column: string): string[] => {
  const condition = conditions.find((item) => item.column === column && item.action === 'in');
  if (!condition?.value) return [];

  if (Array.isArray(condition.value)) {
    return condition.value.map((value) => String(value));
  }

  return [String(condition.value)];
};

const serializeConditions = (conditions: FilterConditionOutput[]) =>
  JSON.stringify(
    conditions.map(({ column, action, value }) => ({
      column,
      action,
      value,
    })),
  );

const parseFiltersFromParams = (value: unknown): FilterConditionOutput[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value as FilterConditionOutput[];
};

const buildSearchFilterFromQuery = (search: string) => {
  const trimmedSearch = search.trim();
  if (!trimmedSearch) {
    return undefined;
  }

  return {
    filters: [{ column: 'ref', operator: 'contains', value: trimmedSearch }],
    logic: 'AND',
  };
};

const fieldToApiMapping: Record<string, string> = {
  id: 'id',
  ref: 'ref',
  order_created_at: 'order_created_at',
  processed_at: 'processed_at',
  last_shipment_update: 'last_shipment_update',
};

export const useOrdersMain = () => {
  const { t } = useTranslation('ordersShippedModule');
  const {
    params: queryParams,
    updateParam,
    updateParams,
  } = useQueryParams({
    search: { type: 'string', default: '' as string },
    filters: { type: 'json', default: [] as FilterConditionOutput[] },
    sortBy: { type: 'string', default: '' as string },
    sortDirection: { type: 'string', default: '' as string },
    shipmentStatus: { type: 'string', default: '' as string, key: 'shipment_status_ids' },
  });

  // Estados de filtros
  const [filterConditions, setFilterConditions] = useState<FilterConditionOutput[]>(() =>
    parseFiltersFromParams(queryParams.filters),
  );
  const filterConditionsSerializedRef = useRef<string>(serializeConditions(filterConditions));
  const [sorting, setSorting] = useState<SortConfig | null>(() => {
    const sortField = queryParams.sortBy;
    if (!sortField) return null;
    return {
      field: sortField,
      direction: queryParams.sortDirection === 'desc' ? 'desc' : 'asc',
    };
  });
  const [statusId, setStatusId] = useState<number | undefined>(undefined);
  {
    /* Estado logístico del envío para filtrado - Shipping logistic status for filtering */
  }
  const [shipmentStatusId, setShipmentStatusId] = useState<number | undefined>(() => {
    if (!queryParams.shipmentStatus) return undefined;
    const parsed = Number(queryParams.shipmentStatus);
    return Number.isNaN(parsed) ? undefined : parsed;
  });
  const [searchFilter, setSearchFilter] = useState<{ filters: any[]; logic: string } | undefined>(() =>
    buildSearchFilterFromQuery(queryParams.search),
  );

  useEffect(() => {
    const nextConditions = parseFiltersFromParams(queryParams.filters);
    const nextSerialized = serializeConditions(nextConditions);
    if (nextSerialized !== filterConditionsSerializedRef.current) {
      filterConditionsSerializedRef.current = nextSerialized;
      setFilterConditions(nextConditions);
    }
  }, [queryParams.filters]);

  useEffect(() => {
    const sortField = queryParams.sortBy;
    if (!sortField) {
      setSorting((prev) => (prev === null ? prev : null));
      return;
    }

    const direction = queryParams.sortDirection === 'desc' ? 'desc' : 'asc';
    setSorting((prev) => {
      if (prev && prev.field === sortField && prev.direction === direction) {
        return prev;
      }

      return {
        field: sortField,
        direction,
      };
    });
  }, [queryParams.sortBy, queryParams.sortDirection]);

  useEffect(() => {
    const normalizedSearch = buildSearchFilterFromQuery(queryParams.search);
    setSearchFilter((prev) => {
      const prevValue = prev?.filters?.[0]?.value ?? '';
      const nextValue = normalizedSearch?.filters?.[0]?.value ?? '';
      return prevValue === nextValue ? prev : normalizedSearch;
    });
  }, [queryParams.search]);

  useEffect(() => {
    const shipmentStatusValue = queryParams.shipmentStatus;
    const parsed = shipmentStatusValue ? Number(shipmentStatusValue) : undefined;
    const normalized = parsed === undefined || Number.isNaN(parsed) ? undefined : parsed;
    setShipmentStatusId((prev) => (prev === normalized ? prev : normalized));
  }, [queryParams.shipmentStatus]);

  // Estado de selección
  const [selectedOrders, setSelectedOrders] = useState<Set<number>>(new Set());

  // Estados de columnas
  const tableColumns = [
    { accessorKey: 'ref', header: t('table.order') },
    { accessorKey: 'products', header: t('table.products') },
    { accessorKey: 'shop', header: t('table.shop') },
    { accessorKey: 'status', header: t('table.status') },
    { accessorKey: 'courier', header: t('table.courier') },
    { accessorKey: 'address', header: t('table.address') },
    { accessorKey: 'actions', header: '' },
  ];

  const [selectedColumns, setSelectedColumns] = useState<string[]>(tableColumns.map((col) => col.accessorKey));

  // Procesar filtros
  const processedFilters = useMemo(() => {
    const searchFilterFromConditions =
      filterConditions.length > 0
        ? {
            filters: filterConditions.map((condition) => {
              const requiresArray = ['in', 'not_in'].includes(condition.action);
              const isNullOperator = ['is_null', 'is_not_null'].includes(condition.action);

              const value = isNullOperator
                ? undefined
                : requiresArray
                  ? Array.isArray(condition.value)
                    ? condition.value
                    : [condition.value]
                  : condition.value;

              return {
                column: condition.column,
                operator: condition.action,
                ...(value !== undefined && { value: value }),
              };
            }),
            logic: 'AND',
          }
        : undefined;

    const event_ids: number[] = [];

    filterConditions.forEach((condition) => {
      if (condition.column === 'event' && condition.action === 'equals') {
        event_ids.push(Number(condition.value));
      }
    });

    // Mapear el campo de sorting a los parámetros de la API
    const apiField = sorting?.field ? fieldToApiMapping[sorting.field] || 'id' : 'id';
    const apiDirection = sorting?.direction || 'desc';

    return {
      ...(event_ids.length === 1 && { event_ids: event_ids[0] }),
      ...(event_ids.length > 1 && { event_ids }),
      sortBy: apiField,
      sortDirection: apiDirection,
      ...(searchFilterFromConditions && {
        searchFilter: searchFilterFromConditions,
      }),
    };
  }, [filterConditions, sorting]);

  const logisticCountsSearchFilter = useMemo(() => {
    const filtersFromConditions = filterConditions.map((condition) => {
      const requiresArray = ['in', 'not_in'].includes(condition.action);
      const isNullOperator = ['is_null', 'is_not_null'].includes(condition.action);
      const value = isNullOperator
        ? undefined
        : requiresArray
          ? Array.isArray(condition.value)
            ? condition.value
            : [condition.value]
          : condition.value;

      return {
        column: condition.column,
        operator: condition.action,
        ...(value !== undefined && { value }),
      };
    });

    const filtersFromSearch = searchFilter?.filters || [];
    const mergedFilters = [...filtersFromConditions, ...filtersFromSearch];

    if (mergedFilters.length === 0) {
      return undefined;
    }

    return {
      filters: mergedFilters,
      logic: 'AND',
    };
  }, [filterConditions, searchFilter]);

  // Hooks de datos
  const {
    orders,
    isLoading,
    isFetching, // 🎯 Para aplicar opacidad durante refetch
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    total,
  } = useOrders({
    searchFilter,
    ...processedFilters,
    status_ids: statusId,
    shipment_status_ids: shipmentStatusId,
  });

  // Hook para conteos de estados logísticos
  const { counts: logisticStatusCounts, isLoading: countsLoading } = useLogisticStatusCounts({
    searchFilter: logisticCountsSearchFilter,
  });

  // Hook para estadísticas usando React Query
  const statsAdapter = new StatsApiAdapter(createHttpClient(apiClientWH));
  const statsQueryParams = useMemo(() => {
    // Prioriza fecha de envío; si no existe, usa fecha de creación.
    const processedAt = getDateFromConditions(filterConditions, 'processed_at');
    const createdAt = getDateFromConditions(filterConditions, 'order_created_at');
    const selectedDateRange = processedAt.from && processedAt.to ? processedAt : createdAt;

    const shopIds = getInConditionValues(filterConditions, 'shop_id');
    const paymentMethodIds = getInConditionValues(filterConditions, 'payment_method_id');
    const productIds = getInConditionValues(filterConditions, 'product_id');
    const courierIds = getInConditionValues(filterConditions, 'courier_id');

    const searchValue =
      searchFilter?.filters?.find((filter: any) => typeof filter?.value === 'string')?.value?.trim() || undefined;

    return {
      ...(selectedDateRange.from && { 'date[start]': selectedDateRange.from }),
      ...(selectedDateRange.to && { 'date[end]': selectedDateRange.to }),
      ...(shopIds.length > 0 && { shop_ids: shopIds.join(',') }),
      ...(paymentMethodIds.length > 0 && { payment_method_ids: paymentMethodIds.join(',') }),
      ...(productIds.length > 0 && { product_ids: productIds.join(',') }),
      ...(courierIds.length > 0 && { pack_ids: courierIds.join(',') }),
      ...(searchValue && { search: searchValue }),
    };
  }, [filterConditions, searchFilter]);

  const {
    data: ordersStats,
    isLoading: statsLoading,
    refetch: fetchStats,
  } = useQuery({
    queryKey: ['ordersStats', statsQueryParams],
    queryFn: () => statsAdapter.getOrdersStats(statsQueryParams),
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
  });

  // Handlers de filtros
  const handleFilterChange = useCallback(
    (conditions: FilterConditionOutput[]) => {
      const serialized = serializeConditions(conditions);
      if (filterConditionsSerializedRef.current === serialized) {
        return;
      }

      filterConditionsSerializedRef.current = serialized;
      setFilterConditions(conditions);
      updateParam('filters', conditions);
    },
    [updateParam],
  );

  const handleSearch = useCallback(
    (filterObj: { filters: any[]; logic: string }) => {
      const searchValue =
        filterObj?.filters?.find((filter: any) => filter?.column === 'ref' && filter?.operator === 'contains')?.value ||
        '';
      const normalizedSearch = String(searchValue).trim();
      setSearchFilter(normalizedSearch ? filterObj : undefined);
      updateParam('search', normalizedSearch);
    },
    [updateParam],
  );

  const handleColumnsChange = useCallback((newSelected: string[]) => {
    setSelectedColumns(newSelected);
  }, []);

  const handleSortingChange = useCallback(
    (newSorting: SortConfig | null) => {
      setSorting(newSorting);

      if (newSorting && newSorting.field) {
        updateParams({
          sortBy: newSorting.field,
          sortDirection: newSorting.direction,
        });
      } else {
        updateParams({
          sortBy: '',
          sortDirection: '',
        });
      }
    },
    [updateParams],
  );

  {
    /* Handler para cambio de estado de envío - Handler for shipping status change */
  }
  const handleShipmentStatusChange = useCallback(
    (statusId?: number) => {
      setShipmentStatusId(statusId);
      updateParam('shipmentStatus', statusId !== undefined ? String(statusId) : '');
    },
    [updateParam],
  );

  const handleClearSelection = useCallback(() => {
    setSelectedOrders(new Set());
  }, []);

  // Handlers de acciones
  const handleCancelSelected = useCallback(() => {
    // TODO: Implementar lógica para cancelar pedidos seleccionados
  }, []);

  const { toast } = useToast();

  const handlePrepareSelected = useCallback(
    async (preparationType: 'picking_packing' | 'massive_packing') => {
      if (selectedOrders.size === 0) return;

      try {
        const httpClient = createHttpClient(apiClientWH);
        const apiAdapter = new OrdersApiAdapter(httpClient);

        const orderIds = Array.from(selectedOrders);
        const preparationTypeNumber = preparationType === 'picking_packing' ? 3 : 1;

        await apiAdapter.markAsPreparation(orderIds, preparationTypeNumber);

        handleClearSelection();
        refetch();

        toast({
          title: t('toast.bulk_preparation_success.title', 'Preparación creada'),
          description: t('toast.bulk_preparation_success.description', {
            count: orderIds.length,
          }),
          variant: 'success',
        });
      } catch (error: any) {
        const apiMessage = error?.response?.data?.message;
        toast({
          title: t('toast.error.title', 'Error'),
          description: apiMessage || t('toast.error.description'),
          variant: 'destructive',
        });
      }
    },
    [selectedOrders, handleClearSelection, refetch, toast, t],
  );

  const handleMarkForSendSelected = useCallback(async () => {
    if (selectedOrders.size === 0) return;

    try {
      const httpClient = createHttpClient(apiClientWH);
      const apiAdapter = new OrdersApiAdapter(httpClient);

      const orderIds = Array.from(selectedOrders);
      await apiAdapter.confirmOrders(orderIds);

      handleClearSelection();
      refetch();
    } catch (error) {
      console.error('Error al confirmar pedidos:', error);
    }
  }, [selectedOrders, handleClearSelection, refetch]);

  const handleUncancelSelected = useCallback(() => {
    // TODO: Implementar lógica para descancelar pedidos seleccionados
  }, []);

  const handleCreateLocation = useCallback(() => {
    // TODO: Implementar lógica para crear pedido
  }, []);

  // Calcular contadores de acciones disponibles
  const actionCounters = useMemo(() => {
    if (selectedOrders.size === 0) {
      return {
        cancelableCount: 0,
        preparableCount: 0,
        sendableCount: 0,
        uncancelableCount: 0,
      };
    }

    // Por ahora, retornar valores por defecto
    // TODO: Implementar lógica específica para pedidos enviados
    return {
      cancelableCount: 0,
      preparableCount: 0,
      sendableCount: 0,
      uncancelableCount: 0,
    };
  }, [selectedOrders.size]);

  // Props para componentes (sin selección múltiple)
  const ordersViewProps = useMemo(
    () => ({
      orders,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      total,
      selectedColumns: [...selectedColumns, 'actions'],
      refetch,
      sorting,
      onSortingChange: handleSortingChange,
    }),
    [
      orders,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      total,
      selectedColumns,
      refetch,
      sorting,
      handleSortingChange,
    ],
  );

  // Detectar si hay filtros activos
  const hasActiveFilters = useMemo(() => {
    return filterConditions.length > 0 || searchFilter !== undefined || statusId !== undefined || sorting !== null;
  }, [filterConditions.length, searchFilter, statusId, sorting]);

  return {
    // Estados
    filterConditions,
    statusId,
    selectedOrders,
    selectedColumns,
    tableColumns,
    sorting,

    // Datos
    orders,
    isLoading,
    isFetching, // 🎯 Para aplicar opacidad durante refetch
    isFetchingNextPage, // 🎯 Para detectar infinite scroll
    error,
    total,

    // Props para infinite scroll
    fetchNextPage,
    hasNextPage,

    // Estadísticas
    ordersStats,
    statsLoading,

    // Conteos de estados logísticos
    logisticStatusCounts,
    countsLoading,

    // Contadores
    actionCounters,

    // Props para componentes
    ordersViewProps,

    // Handlers
    setStatusId,
    handleShipmentStatusChange,
    handleFilterChange,
    handleSearch,
    handleColumnsChange,
    handleSortingChange,
    handleCancelSelected,
    handlePrepareSelected,
    handleMarkForSendSelected,
    handleUncancelSelected,
    handleCreateLocation,
    handleClearSelection,
    refetch,
    fetchStats,

    // Estado de filtros
    hasActiveFilters,
    searchFilter,
    shipmentStatusId,
  };
};
